import { useContext, useLayoutEffect, useState, useRef } from 'react'
import { TrainingCalendarContext } from 'context/TrainingCalendarContext'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import moment from 'moment'

import ContentFrame from 'components/Frame/ContentFrame'
import TrainingCalendarDay from 'components/Training/TrainingCalendar/TrainingCalendarDay'

export default function TrainingCalendar({ 
    clientDate,
    trainingCalendarTimescale = 'week',
    propProfileType = 'individual',
    kinesisProfileDetails = null,
    urlGroupId = null,
    urlUserId = null,
    selectedWeekOfTheYearKey = null,
    setSelectedWeekOfTheYearKey = null,
    trainingCalendarData = null,
    trainingCalendarDateRange = [],
    LoadTrainingCalendarData = () => {},
    UpdateKinesisWorkoutCalendarItemDate = null
}) {

    const { exerciseHashMap } = useGetExercises()

    const clientDateMoment = moment(clientDate, 'YYYY-MM-DD')

    const { UpdateTrainingCalendarDateRange } = useContext(TrainingCalendarContext)

    const [draggedKinesisWorkout, setDraggedKinesisWorkout] = useState(null)

    const previousWeekStartDateRef = useRef(null)
    const weekSlideDirectionRef = useRef(null)

    // Layout effects so the date range sync and the load start (which clears the old data) happen before paint. Otherwise the new week briefly paints with stale data

    useLayoutEffect(() => {
        if (!selectedWeekOfTheYearKey) return
        UpdateTrainingCalendarDateRange(new WeekOfTheYearKey(selectedWeekOfTheYearKey).toDate().startOf('day'))
    }, [selectedWeekOfTheYearKey, UpdateTrainingCalendarDateRange])

    // The date range lives in TrainingCalendarContext and can still be from a previous week (or profile visit) until the effect above syncs it.
    // Only load once it matches the selected week, and key on the dates instead of the array reference so an identical range does not reload

    const trainingCalendarDateRangeKey = trainingCalendarDateRange.length === 0 ? null : (
        trainingCalendarDateRange[0].format('YYYY-MM-DD') + '_' + trainingCalendarDateRange[trainingCalendarDateRange.length - 1].format('YYYY-MM-DD')
    )

    const isTrainingCalendarDateRangeSynced = (
        trainingCalendarDateRangeKey !== null
        && (!selectedWeekOfTheYearKey || trainingCalendarDateRange[0].isSame(new WeekOfTheYearKey(selectedWeekOfTheYearKey).toDate(), 'isoWeek'))
    )

    // Any change aborts the previous load. StrictMode's mount/unmount/mount aborts the first load before it is sent,
    // since WhichWeightAPI's async auth interceptor runs before axios checks the signal and dispatches the request

    useLayoutEffect(() => {
        if (!isTrainingCalendarDateRangeSynced) return

        const abortController = new AbortController()

        LoadTrainingCalendarData(abortController.signal)

        return () => abortController.abort()
    }, [urlGroupId, urlUserId, trainingCalendarDateRangeKey, isTrainingCalendarDateRangeSynced, trainingCalendarTimescale])

    // Same action as the arrow keys

    // Slide the week in from the side it came from. The direction is only updated when the week changes (not on the first render or a re-render),
    // and the week grid below is keyed on its start date so the animation replays on each week change

    const weekStartDate = trainingCalendarDateRange[0]?.format('YYYY-MM-DD') ?? null

    if (weekStartDate !== previousWeekStartDateRef.current) {
        if (previousWeekStartDateRef.current !== null && weekStartDate !== null) {
            weekSlideDirectionRef.current = weekStartDate > previousWeekStartDateRef.current ? 'right' : 'left'
        }

        previousWeekStartDateRef.current = weekStartDate
    }

    const NavigateWeek = (weekDelta) => {
        setSelectedWeekOfTheYearKey((currentWeekOfTheYearKey) => (new WeekOfTheYearKey(currentWeekOfTheYearKey).add(weekDelta).toString()))
    }

    if (Object.keys(exerciseHashMap).length === 0) {
        return null
    }

    if (trainingCalendarTimescale === 'block') {
        
        const weekdayHeaders = trainingCalendarDateRange.slice(0, 7)
        
        const weekRows = []

        for (let i = 0; i < trainingCalendarDateRange.length; i += 7) {
            weekRows.push(trainingCalendarDateRange.slice(i, i + 7))
        }

        return (
            <ContentFrame className='flex h-full min-h-0 flex-col'>
                <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col select-none">
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
                        <div className="grid shrink-0 grid-cols-7 divide-x divide-gray-200 border-b border-gray-200 bg-white">
                            {weekdayHeaders.map((date, index) => (
                                <div
                                    key={index}
                                    className="px-2 py-1 2xl:py-2 text-center tracking-wide font-bold text-xxs 2xl:text-xs"
                                >
                                    {date.format('dddd')}
                                </div>
                            ))}
                        </div>

                        <div className="min-h-0 flex-1 overflow-auto">
                            <div className="flex h-full min-h-0 w-full min-w-0 flex-col divide-y divide-gray-200">
                                {weekRows.map((weekDates, weekIndex) => {

                                    const weekDate = weekDates[0]
                                    
                                    const isDateBeforeClientISOWeek = weekDate.isBefore(clientDateMoment, 'isoWeek')
                                    const isDateInClientISOWeek = weekDate.isSame(clientDateMoment, 'isoWeek')
                                    const isDateAfterClientISOWeek = weekDate.isAfter(clientDateMoment, 'isoWeek')

                                    return (
                                        <div
                                            key={weekIndex}
                                            className="relative grid min-h-0 min-w-0 flex-1 divide-x divide-gray-200"
                                            style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
                                        >
                                            {/* Present week orange highlight */}
                                            {/* 
                                            {isDateInClientISOWeek && (
                                                <div className="pointer-events-none absolute inset-0 z-10 border-2 border-orange-400" />
                                            )} 
                                            */}

                                            {weekDates.map((date, dayIndex) => (
                                                <TrainingCalendarDay
                                                    key={dayIndex}
                                                    propProfileType={propProfileType}
                                                    kinesisUserGroupId={urlGroupId}
                                                    userId={urlUserId}
                                                    kinesisProfileDetails={kinesisProfileDetails}
                                                    clientDate={clientDate}
                                                    timescale={trainingCalendarTimescale}
                                                    date={date}
                                                    isDateBeforeClientISOWeek={isDateBeforeClientISOWeek}
                                                    isDateInClientISOWeek={isDateInClientISOWeek}
                                                    isDateAfterClientISOWeek={isDateAfterClientISOWeek}
                                                    trainingCalendarData={trainingCalendarData}
                                                    exerciseHashMap={exerciseHashMap}
                                                    draggedKinesisWorkout={draggedKinesisWorkout}
                                                    setDraggedKinesisWorkout={setDraggedKinesisWorkout}
                                                    UpdateKinesisWorkoutCalendarItemDate={UpdateKinesisWorkoutCalendarItemDate}
                                                    OnWorkoutOneOffOverridesSaved={() => LoadTrainingCalendarData()}
                                                />
                                            ))}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </ContentFrame>
        )
    }

    if (trainingCalendarTimescale === 'week') {

        const weekDate = trainingCalendarDateRange[0]
        
        const isDateBeforeClientISOWeek = weekDate.isBefore(clientDateMoment, 'isoWeek')
        const isDateInClientISOWeek = weekDate.isSame(clientDateMoment, 'isoWeek')
        const isDateAfterClientISOWeek = weekDate.isAfter(clientDateMoment, 'isoWeek')

        return (
            <ContentFrame className='flex h-full min-h-0 flex-col'>
                <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-row divide-x divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white select-none">
                    {setSelectedWeekOfTheYearKey !== null && (
                        <button
                            className='flex w-8 shrink-0 items-center justify-center bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                            onClick={() => NavigateWeek(-1)}
                        >
                            <ChevronLeftIcon className='h-5 w-5' />
                        </button>
                    )}

                    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
                        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                            <div
                                key={weekStartDate}
                                className={`
                                    relative grid h-full min-h-0 w-full min-w-0 flex-1 divide-x divide-gray-200
                                    ${weekSlideDirectionRef.current === 'right' ? 'animate-slide-in-from-right' : ''}
                                    ${weekSlideDirectionRef.current === 'left' ? 'animate-slide-in-from-left' : ''}
                                `}
                                style={{
                                    gridTemplateColumns: `repeat(${trainingCalendarDateRange.length}, minmax(0, 1fr))`
                                }}
                            >
                                {/* 
                                {isDateInClientISOWeek && (
                                    <div className="pointer-events-none absolute inset-0 z-10 border-2 border-orange-400" />
                                )} 
                                */}

                                {trainingCalendarDateRange.map((date, index) => (
                                    <TrainingCalendarDay
                                        key={index}
                                        propProfileType={propProfileType}
                                        kinesisUserGroupId={urlGroupId}
                                        userId={urlUserId}
                                        kinesisProfileDetails={kinesisProfileDetails}
                                        clientDate={clientDate}
                                        timescale={trainingCalendarTimescale}
                                        date={date}
                                        isDateBeforeClientISOWeek={isDateBeforeClientISOWeek}
                                        isDateInClientISOWeek={isDateInClientISOWeek}
                                        isDateAfterClientISOWeek={isDateAfterClientISOWeek}
                                        trainingCalendarData={trainingCalendarData}
                                        exerciseHashMap={exerciseHashMap}
                                        draggedKinesisWorkout={draggedKinesisWorkout}
                                        setDraggedKinesisWorkout={setDraggedKinesisWorkout}
                                        UpdateKinesisWorkoutCalendarItemDate={UpdateKinesisWorkoutCalendarItemDate}
                                        OnWorkoutOneOffOverridesSaved={() => LoadTrainingCalendarData()}
                                    />
                                ))} 
                            </div>
                        </div>
                    </div>

                    {setSelectedWeekOfTheYearKey !== null && (
                        <button
                            className='flex w-8 shrink-0 items-center justify-center bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                            onClick={() => NavigateWeek(1)}
                        >
                            <ChevronRightIcon className='h-5 w-5' />
                        </button>
                    )}
                </div>
            </ContentFrame>
        )
    }

    return (
        <div>{trainingCalendarTimescale} not supported yet!</div>
    )
}
